import React, { useState, useEffect } from "react";
import {
  ProSidebar,
  Menu,
  MenuItem,
  SubMenu,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
} from "react-pro-sidebar";
import { useNavigate } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { FiHome, FiLogOut, FiUsers, FiMusic, FiUser, FiUserCheck, FiArrowRightCircle, FiArrowLeftCircle } from "react-icons/fi";
import { GoArrowSwitch } from "react-icons/go";
import { BiHelpCircle } from "react-icons/bi";
import { HiOutlineDocument } from "react-icons/hi";
import { ProfileUserNew } from "assets";
import { getCookie } from "services/cookies";
import { API_POST_MAMBOO, API_GET_MAMBOO } from "services";
import useLoginStore from "hooks/useLoginStore";
import ThemeToggle from "components/ThemeToggle";
import { resolvePhotoUrl } from "utils/photo";
import { TbCircleDashed } from "react-icons/tb";
import { DRAFT_KONTRAK_PATH, KATALOG_LAGU_PATH, REGISTER_REQ_PATH } from "utils/paths";
import "./sidebar.scss";

const SidebarComponent = ({ onNavigate, sidebarOpen }) => {
  const navigate = useNavigate();
  const [menuCollapse, setMenuCollapse] = useState(false);
  const [isPhotoBroken, setIsPhotoBroken] = useState(false);
  const [showPages, setShowPages] = useState({
    anggota: false,
    katalogLagu: false,
    transaksi: false,
    laporan: false,
    superAdmin: false
  })
  const { serverProfile, isLoadingServerProfile, setIsVerified, deleteProfile } = useLoginStore();

  // Gating menu selalu memakai peran dari server, bukan dari localStorage.
  // Selama peran belum dipastikan, menu ber-gating disembunyikan dulu supaya
  // tidak sempat terlihat oleh peran yang tidak berhak.
  const isPencipta = serverProfile?.role_code == 3;
  const isAdmin = serverProfile?.role_code == 2;
  const isRoleUnconfirmed = isLoadingServerProfile || !serverProfile;

  // Selama mamboo_id belum ada, user dikunci di halaman draft kontrak:
  // seluruh menu lain disembunyikan. Logout tetap tersedia di bagian bawah
  // supaya user tidak terjebak.
  const isOnboarding = !isRoleUnconfirmed && !serverProfile?.mamboo_id;
  const isMenuHidden = isRoleUnconfirmed || isOnboarding;

  // Cookie hanya menyimpan string, jadi nilai kosong bisa berupa "", "null",
  // atau "undefined" — semuanya dianggap tidak punya foto oleh resolvePhotoUrl.
  const photoProfileUrl = resolvePhotoUrl(getCookie("photo_profile"));

  const pathname = window.location.pathname;
  // Submenu Katalog Lagu langsung terbuka bila halaman yang dibuka ada di
  // bawahnya (mis. setelah refresh), supaya item aktifnya kelihatan.
  const isKatalogLaguRoute = pathname.startsWith(KATALOG_LAGU_PATH);

  const menuIconClick = () => {
    menuCollapse ? setMenuCollapse(false) : setMenuCollapse(true);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const handleLogout = () => {
    // Sesi lokal sudah dibersihkan di dalam doSignOut, baik berhasil maupun gagal.
    // Jadi user tetap diarahkan ke halaman login walaupun API logout error.
    const finishLogout = () => {
      deleteProfile();
      navigate('/auth/login');
      if (onNavigate) onNavigate();
    };

    API_POST_MAMBOO.actions.doSignOut().then(finishLogout).catch(finishLogout);
  }

  useEffect(() => {
    // Saat logout, cookie sudah terhapus dan efek ini sempat jalan sekali lagi
    // sebelum sidebar di-unmount. Tanpa penjagaan ini requestnya dikirim tanpa
    // token dan berujung error yang tidak tertangani.
    if (!getCookie('auth_token')) return;

    API_GET_MAMBOO.dynamic.getDashboard()
      .then((resp) => {
        setIsVerified(resp.is_verified)
      })
      .catch(() => {
        setIsVerified(false)
      })
  }, [getCookie('photo_profile')])

  // Daftar akses ikut dibaca dari profil server, bukan dari localStorage.
  useEffect(() => {
    serverProfile?.access?.map((val) => {
      if(val.access_name === "Transaksi") {
        setShowPages((prevState) => ({...prevState, transaksi: true}))
      }

      if(val.access_name === "Laporan") {
        setShowPages((prevState) => ({...prevState, laporan: true}))
      }

      if(val.access_name === "Katalog Lagu") {
        setShowPages((prevState) => ({...prevState, katalogLagu: true}))
      }

      if(val.access_name === "Kelola Anggota") {
        setShowPages((prevState) => ({...prevState, anggota: true}))
      }

      if (val.access_name === "Super Admin") {
        setShowPages((prevState) => ({
          ...prevState,
          transaksi: true,
          laporan: true,
          katalogLagu: true,
          anggota: true,
          superAdmin: true
        }));
      }
    })
  }, [serverProfile])

  return (
    <>
      <div id="header" className={sidebarOpen ? 'sidebar-open' : ''}>
        <ProSidebar collapsed={menuCollapse}>
          <SidebarHeader>
            <div className="head-logo">
              {photoProfileUrl && !isPhotoBroken ? (
                <>
                  <img
                    src={photoProfileUrl}
                    className="sidebar-avatar"
                    alt="PhotoProfile-1"
                    // Foto Google (lh3.googleusercontent.com) membalas 403 bila
                    // request membawa header Referer. Tanpa ini fotonya gagal dimuat.
                    referrerPolicy="no-referrer"
                    // Kalau URL-nya tetap gagal dimuat, jatuh ke gambar bawaan
                    // daripada menampilkan ikon gambar rusak.
                    onError={() => setIsPhotoBroken(true)}
                  />
                  <span className="text-logo">{getCookie("profile")}</span>
                </>
              ) : (
                <>
                  <img
                    src={ProfileUserNew}
                    className="sidebar-avatar is-placeholder"
                    alt="PhotoProfile-2"
                  />
                  <span className="text-logo">{getCookie("profile")}</span>
                </>
              )}
            </div>
          </SidebarHeader>
          <SidebarContent>
            <Menu iconShape="square">
              <MenuItem
                active={window.location.pathname === DRAFT_KONTRAK_PATH}
                onClick={() => handleNavigate(DRAFT_KONTRAK_PATH)}
                icon={<HiOutlineDocument />}
                hidden={!isOnboarding}
              >
                Draft Kontrak
              </MenuItem>
              <MenuItem
                active={window.location.pathname === "/"}
                onClick={() => handleNavigate("/")}
                icon={<FiHome />}
                hidden={isMenuHidden}
              >
                Dashboard
              </MenuItem>
              <SubMenu
                title="Anggota"
                icon={<FiUsers />}
                hidden={
                  isMenuHidden ||
                  isPencipta ||
                  (isAdmin && !showPages.anggota)
                }
              >
                <MenuItem
                  active={window.location.pathname === "/pencipta"}
                  icon={<FiUsers />}
                  onClick={() => handleNavigate("/pencipta")}
                >
                  Songwriter
                </MenuItem>
                {isAdmin && !showPages.superAdmin ? null : (
                  <MenuItem
                    active={window.location.pathname === "/admin"}
                    icon={<FiUser />}
                    onClick={() => handleNavigate("/admin")}
                  >
                    Admin
                  </MenuItem>
                )}
              </SubMenu>
              {/* Berlaku untuk semua peran, jadi satu-satunya penyembunyian
                  adalah isMenuHidden — yang menutup seluruh menu selama masa
                  onboarding dan selagi peran belum dipastikan. Sengaja tidak
                  diletakkan di dalam submenu Anggota: submenu itu tertutup
                  untuk pencipta, sedangkan halaman ini tidak. */}
              <MenuItem
                active={window.location.pathname === "/ahli-waris"}
                onClick={() => handleNavigate("/ahli-waris")}
                icon={<FiUserCheck />}
                hidden={isMenuHidden}
              >
                Ahli Waris
              </MenuItem>
              <SubMenu
                title="Katalog Lagu"
                icon={<FiMusic />}
                defaultOpen={isKatalogLaguRoute}
                hidden={isMenuHidden || (isAdmin && !showPages.katalogLagu)}
              >
                <MenuItem
                  active={pathname === KATALOG_LAGU_PATH}
                  icon={<TbCircleDashed />}
                  onClick={() => handleNavigate(KATALOG_LAGU_PATH)}
                >
                  List Lagu
                </MenuItem>
                <MenuItem
                  active={pathname === REGISTER_REQ_PATH}
                  icon={<TbCircleDashed />}
                  onClick={() => handleNavigate(REGISTER_REQ_PATH)}
                >
                  Register Req
                </MenuItem>
              </SubMenu>
              <MenuItem
                icon={<GoArrowSwitch />}
                active={window.location.pathname === "/transaksi"}
                onClick={() => handleNavigate("/transaksi")}
                hidden={isMenuHidden || (isAdmin && !showPages.transaksi)}
              >
                Transaksi
              </MenuItem>
              <SubMenu
                title="Laporan"
                active={true}
                icon={<HiOutlineDocument />}
                hidden={isMenuHidden || (isAdmin && !showPages.laporan)}
              >
                <MenuItem
                  active={window.location.pathname === "/distribusi"}
                  onClick={() => handleNavigate("/distribusi")}
                  icon={<HiOutlineDocument />}
                >
                  Distribusi
                </MenuItem>
                <MenuItem
                  active={window.location.pathname === "/pendapatan"}
                  onClick={() => handleNavigate("/pendapatan")}
                  icon={<HiOutlineDocument />}
                >
                  Pendapatan
                </MenuItem>
                {/* Pembacaan & pemetaan laporan DSP. Belum menyimpan ke
                    database, karena itu labelnya menyebutkan statusnya. */}
                <MenuItem
                  active={window.location.pathname === "/distribusi-dsp"}
                  onClick={() => handleNavigate("/distribusi-dsp")}
                  icon={<HiOutlineDocument />}
                >
                  Distribusi (under dev)
                </MenuItem>
              </SubMenu>
              <MenuItem
                active={window.location.pathname === "/profile"}
                icon={<FiUser />}
                onClick={() => handleNavigate("/profile")}
                hidden={isMenuHidden}
              >
                Profile
              </MenuItem>
              <MenuItem
                active={window.location.pathname === "/bantuan"}
                icon={<BiHelpCircle />}
                onClick={() => handleNavigate("/bantuan")}
                hidden={isMenuHidden}
              >
                Bantuan
              </MenuItem>
            </Menu>
          </SidebarContent>
          <SidebarFooter>
            {/* Sakelar tema diletakkan di atas Logout: ia setelan tampilan,
                bukan navigasi, jadi tidak ikut dalam daftar menu halaman. */}
            <ThemeToggle />
            <Menu iconShape="square">
              <MenuItem icon={<FiLogOut />} onClick={handleLogout}>
                Logout
              </MenuItem>
            </Menu>
          </SidebarFooter>
        </ProSidebar>
      </div>
    </>
  );
};

export default SidebarComponent;
